'use client'

import Image from 'next/image'
import { useLocale } from 'next-intl'
import { parsePhoneNumber } from 'libphonenumber-js'
import phoneIcon from '/public/assets/images/phones/phone-black.svg'
import { useBreakpoints } from '@/utils/breakpoints'
import { useQuery } from '@apollo/client'
import {
  GET_COUNTRY_CONFIG,
  CountryConfigDataType
} from '@/graphql/queries/countryConfig'
import { CountryConfig } from '@/types/countryConfig'
import { useSelector } from '@/hooks/redux'
import { CountryType } from '@/reducers/workflow'

const DEFAULT_PHONE_BY_COUNTRY: Record<'FR' | 'BE', string> = {
  FR: '+33183643107',
  BE: '+3228973469'
}

const defaultFrPhone = parsePhoneNumber(DEFAULT_PHONE_BY_COUNTRY.FR)

export const phoneNumber = defaultFrPhone.formatNational()
export const phoneHref = defaultFrPhone.getURI()

const resolveHotlinePhone = (
  countryConfig?: CountryConfig,
  fallbackCountry: CountryType = 'FR'
): string => {
  if (countryConfig?.phone) return countryConfig.phone

  const countryCode = (countryConfig?.countryCode ??
    fallbackCountry) as keyof typeof DEFAULT_PHONE_BY_COUNTRY

  return DEFAULT_PHONE_BY_COUNTRY[countryCode] ?? DEFAULT_PHONE_BY_COUNTRY.FR
}

interface PhoneButtonProps {
  label: string
  className?: string
}

const PhoneLink: React.FC<PhoneButtonProps> = ({ label, className }) => {
  const locale = useLocale()
  const { isWide, isTablet } = useBreakpoints()
  const { data } = useQuery<CountryConfigDataType>(GET_COUNTRY_CONFIG)
  const workflowCountry = useSelector((state) => state.workflow.country) || 'FR'

  const phoneFromConfig = resolveHotlinePhone(
    data?.countryConfig,
    workflowCountry
  )

  let parsedPhone
  try {
    parsedPhone = parsePhoneNumber(phoneFromConfig)
  } catch {
    parsedPhone = parsePhoneNumber(
      resolveHotlinePhone(undefined, workflowCountry)
    )
  }

  const href = parsedPhone.getURI()
  const nationalPhoneNumber = parsedPhone.formatNational()
  const internationalPhoneNumber = parsedPhone.formatInternational()
  const displayedPhoneNumber = locale.startsWith('fr')
    ? nationalPhoneNumber
    : internationalPhoneNumber

  const getLabel = () => {
    if (isWide) {
      return `${label} ${displayedPhoneNumber}`
    } else if (isTablet) {
      return displayedPhoneNumber
    }
    return
  }

  const phoneLinkClassName = className
    ? `p-1-5 bg-neutral-150 flex items-center gap-1-5 rounded-full w-fit hover:opacity-70 body-2 ${className}`
    : 'p-1-5 bg-neutral-150 flex items-center gap-1-5 rounded-full w-fit hover:opacity-70 body-2'

  return (
    <a data-testid='phone-link' href={href} className={phoneLinkClassName}>
      <Image src={phoneIcon} alt='phone icon' />
      {getLabel()}
    </a>
  )
}

export default PhoneLink
